import { getToken } from "./service";
import axios from "axios";
import qs from "qs";
import Config from "../config";
import Storage from "../utils/Storage";

// 签到方式
export enum SignType {
    QrCode = 0, // 二维码
    Number = 1, // 数字
    Normal = 2, // 普通
    Live = 3 //直播
}

// 签到开启状态
export enum SignStatus {
    Idle = 0, // 未开启
    InProgress = 1, // 进行中
    Done = 2 // 结束
}

export const SignStatusMap: { [key: string]: string } = {
    [SignStatus.Idle]: "未开始",
    [SignStatus.InProgress]: "进行中",
    [SignStatus.Done]: "已结束"
};

/**
 * 教师签到列表
 */
export interface SignInListItem {
    id: number;
    title: string;
    type: SignType; // 签到类型0：直播 1：随机数 2:普通签到
    refreshTime: number;
    validityTime: number;
    status: SignStatus; // 签到状态0:未开启 1:进行中 2:结束
    startTime: string;
    endTime: string;
    courseStuCount: number; // 课程人数
    signInStuCount: number; // 以签到人数
    notSingInStuCount: number; // 未签到人数
}

/**
 * 教师签到列表
 */
export function getTeaSignInList(weekTimeId: number) {
    const token = getToken();
    return axios
        .post<SuccessData<SignInListItem[]>>(
            `/teacher/signIn/getTeaSignInList`,
            qs.stringify({ weekTimeId }),
            { headers: { token } }
        )
        .then(res => res.data.data);
}

// 学生签到状态
export enum StuSignInStatus {
    Idle = 0, // 未签到
    Done = 1, // 出勤
    // Absent = 2, // 缺勤
    Late = 3, // 迟到
    Leave = 4, // 早退
    Unreachable = 5 // 请假
}

export const StuSignInMap = {
    [StuSignInStatus.Idle]: "--",
    [StuSignInStatus.Done]: "出勤",
    // [StuSignInStatus.Absent]: "缺勤",
    [StuSignInStatus.Late]: "迟到",
    [StuSignInStatus.Leave]: "早退",
    [StuSignInStatus.Unreachable]: "请假"
};

export interface StuSignIn {
    signStatus: StuSignInStatus; // 学员签到状态  0:未签到 1：出勤 2：缺勤 3：迟到 4：早退
    name: string;
    studentno: string; // 学号
    signInTime: string;
    department: string;
    subject: string; // 专业
    className: string;
}

/**
 * 学生签到名单
 * @param signInId
 */
export function getStuSignInList(signInId: number) {
    const token = getToken();
    return axios
        .post<SuccessData<StuSignIn[]>>(
            `/teacher/signIn/getStuSignInList`,
            qs.stringify({ signInId }),
            { headers: { token } }
        )
        .then(res => res.data.data);
}

/**
 * 直播签到学生列表
 * @param signInId
 */
export function getStuOnlineSignInList(signInId: number) {
    const token = getToken();
    return axios
        .post<SuccessData<StuSignIn[]>>(
            `/teacher/signIn/getStuOnlineSignInList`,
            qs.stringify({ signInId }),
            { headers: { token } }
        )
        .then(res => res.data.data)
        .then(data =>
            data.map(item => ({ ...item, signStatus: item.signStatus || 0 }))
        );
}

/**
 * 刪除签到信息
 * @param signInId
 * @param weekTimeId
 */
export function removeSignIn(signInId: number, weekTimeId: number) {
    const token = getToken();
    return axios
        .post<SuccessData<any>>(
            `/teacher/signIn/removeSignIn`,
            qs.stringify({ signInId, weekTimeId }),
            { headers: { token } }
        )
        .then(res => res.data.data);
}

export interface SignIn {
    type: SignType;
    title: string;
    weekTimeId: number;
    refreshTime: number;
    validityTime: number;
    id?: number;
    status?: number;
}

/**
 * 创建修改签到活动
 * @param params
 */
export function createOrUpdateSignIn(params: SignIn) {
    const token = getToken();
    return axios
        .post<SuccessData<any>>(
            `/teacher/signIn/createOrUpdateSignIn`,
            qs.stringify(params),
            { headers: { token } }
        )
        .then(res => res.data);
}

export interface SignInDetail {
    id: number;
    title: string;
    weekTimeId: number;
    type: SignType;
    refreshTime: number;
    validityTime: number;
    status: SignStatus;
    delStatus: number;
    createUser: string;
    updateUser: number;
    createTime: string;
    updateTime: string;
    startTime: string;
    code: string;
}
/**
 * 签到活动明细
 * @param signInId
 */
export function getTeaSignIn(signInId: number) {
    const token = getToken();
    return axios
        .post<SuccessData<SignInDetail>>(
            `/teacher/signIn/getTeaSignIn`,
            qs.stringify({ signInId }),
            { headers: { token } }
        )
        .then(res => res.data.data);
}

export interface SignInInfo {
    id: number;
    title: string;
    weekTimeId: number;
    type: SignType;
    refreshTime: number;
    validityTime: number;
    status: SignStatus;
    delStatus: number;
    createUser: string;
    updateUser: number;
    createTime: string;
    updateTime: string;
    startTime: string;
    endTime: string;
    code: string;
    time: string; // 右上角的时间(分钟)
    percentage: string; // 出勤率
}

// 签到详情中的签到明细
export function getSignInfo(signInId: number) {
    const token = getToken();
    return axios
        .post<SuccessData<SignInInfo>>(
            `/teacher/signIn/getSignInfo`,
            qs.stringify({ signInId }),
            { headers: { token } }
        )
        .then(res => res.data.data);
}

// 开启普通签到
export function createPlain(signInId: number) {
    const token = getToken();
    return axios.post<SuccessData<SignInInfo>>(
        `/teacher/signIn/createPlain`,
        qs.stringify({ signInId }),
        { headers: { token } }
    );
}

// 开启数字签到
export function createNumberCode(signInId: number) {
    const token = getToken();
    return axios.post<SuccessData<SignInInfo>>(
        `/teacher/signIn/createNumberCode`,
        qs.stringify({ signInId }),
        { headers: { token } }
    );
}

// 结束签到活动
export function signInEnd(signInId: number) {
    const token = getToken();
    return axios.post<SuccessData<SignInInfo>>(
        `/teacher/signIn/signInEnd`,
        qs.stringify({ signInId }),
        { headers: { token } }
    );
}

// 设置学生签到状态
export function updateStuSignStatus(params: {
    signInId: number;
    studentno: string;
    signStatus: StuSignInStatus;
}) {
    const token = getToken();
    return axios.post<SuccessData<SignInInfo>>(
        `/teacher/signIn/updateStuSignStatus`,
        qs.stringify(params),
        { headers: { token } }
    );
}

// 直播签到修改学生状态
export function updateStuOnLineSignStatus(params: {
    signInId: number;
    studentno: string;
    signStatus: StuSignInStatus;
}) {
    const token = getToken();
    return axios.post<SuccessData<SignInInfo>>(
        `/teacher/signIn/updateStuOnLineSignStatus`,
        qs.stringify(params),
        { headers: { token } }
    );
}

// 导出签到情况
export function exportSignInStu(signInId: number, type?: SignType) {
    const token = encodeURIComponent(Storage.get("token") as string);
    if (type === SignType.Live) {
        window.open(
            `${Config.ApiHost}teacher/signIn/exportOnlineSignInStu?signInId=${signInId}&token=${token}`
        );
    } else {
        window.open(
            `${Config.ApiHost}teacher/signIn/exportSignInStu?signInId=${signInId}&token=${token}`
        );
    }
}

// 设置学生签到状态
export function freshNumberCode(signInId: number) {
    const token = getToken();
    return axios
        .post<SuccessData<number>>(
            `/teacher/signIn/freshNumberCode`,
            qs.stringify({ signInId }),
            { headers: { token } }
        )
        .then(res => res.data.data);
}

//同步直播签到数据
export function saveCourseSignRecord(weekTimeId: number) {
    const token = getToken();
    return axios
        .post(
            `/teacher/signIn/saveCourseSignRecord`,
            qs.stringify({ weekTimeId }),
            { headers: { token } }
        )
        .finally(() => window.location.reload());
}

/**
 * 一键更新为出勤
 * @param signInId
 */
export function updateAllStuSignStatus(signInId: number) {
    return axios.post(
        "/teacher/signIn/updateAllStuSignStatus",
        qs.stringify({ signInId })
    );
}
